import React, { useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { toast } from 'react-toastify';
import './CoachingSurveyModal.scss';
import { useGetSurveyQuestionsQuery, useSubmitSurveyResponseMutation } from '../../coachingActionSlice';

interface CoachingSurveyModalProps {
    visible: boolean;
    onHide: () => void;
    coachingActionPlanId: number;
    supervisorNumber: string;
    metricName?: string;
    onSubmitSuccess?: () => void;
}

export const CoachingSurveyModal: React.FC<CoachingSurveyModalProps> = ({
    visible,
    onHide,
    coachingActionPlanId,
    supervisorNumber,
    metricName,
    onSubmitSuccess,
}) => {
    const { data: questionsData, isLoading } = useGetSurveyQuestionsQuery();
    const [submitSurvey, { isLoading: isSubmitting }] = useSubmitSurveyResponseMutation();

    const [ratings, setRatings] = useState<Record<number, number>>({});

    const questions = questionsData?.data ?? [];

    const handleRating = (questionId: number, value: number) => {
        setRatings((prev) => ({ ...prev, [questionId]: value }));
    };

const handleSubmit = async () => {
    try {
        await submitSurvey({
            coachingActionPlanId,
            supervisorNumber,
            responses: questions.map((q: any) => ({
                questionId: q.coaching_survey_question_id as number,
                rating:     ratings[q.coaching_survey_question_id] ?? 0,
            })),
        }).unwrap();
        toast.success('Survey submitted successfully.');
        onHide();
        onSubmitSuccess?.();
    } catch {
        toast.error('Failed to submit survey. Please try again.');
    }
};

    const renderStars = (question: any) => {
        const maxRating = question.max_rating ?? 3;
        const qId       = question.coaching_survey_question_id;
        const selected  = ratings[qId] ?? 0;

        return (
            <div className="survey-stars">
                {Array.from({ length: maxRating }, (_, i) => i + 1).map((star) => (
                    <i
                        key={star}
                        className={`pi ${star <= selected ? 'pi-star-fill survey-star--filled' : 'pi-star survey-star--empty'}`}
                        onClick={() => handleRating(qId, star)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && handleRating(qId, star)}
                    />
                ))}
            </div>
        );
    };

    return (
        <Dialog
            visible={visible}
            onHide={onHide}
            modal
            draggable={false}
            resizable={false}
            closable={false}
            showHeader={false}
            className="coaching-survey-dialog"
            style={{ width: '1150px' }}
        >
            <div className="coaching-survey">
                <div className="coaching-survey__header">
                    <div className="coaching-survey__title-row">
                        <h2 className="coaching-survey__title">Coaching Survey</h2>
                        {metricName && (
                            <span className="coaching-survey__metric">
                                Metric Coached: {metricName}
                            </span>
                        )}
                    </div>
                    <p className="coaching-survey__subtitle">
                        Your responses will not be visible to your Team Lead
                    </p>
                    <div className="coaching-survey__divider" />
                </div>

                <div className="coaching-survey__body">
                    {isLoading ? (
                        <div className="coaching-survey__loading">
                            <i className="pi pi-spin pi-spinner" />
                        </div>
                    ) : (
                        questions.map((q: any, idx: number) => (
                            <div key={q.coaching_survey_question_id} className="coaching-survey__question">
                                <p className="coaching-survey__question-text">{q.question_text}</p>
                                {renderStars(q)}
                                {idx < questions.length - 1 && (
                                    <div className="coaching-survey__divider" />
                                )}
                            </div>
                        ))
                    )}
                </div>

                <div className="coaching-survey__divider" />
                <div className="coaching-survey__footer">
                    <Button
                        label="Skip"
                        icon="pi pi-times-circle"
                        className="coaching-survey__btn-skip"
                        onClick={onHide}
                    />
                    <Button
                        label="Submit"
                        icon="pi pi-check-circle"
                        className="coaching-survey__btn-submit"
                        onClick={handleSubmit}
                        loading={isSubmitting}
                        disabled={isSubmitting}
                    />
                </div>
            </div>
        </Dialog>
    );
};